import { useEffect, useState } from "react";
import { getStats } from "./api";
import { Envelope } from "./types";

export interface StatsError { kind: "forbidden" | "timeout" | "bad-request" | "failed"; message: string }
export interface StatsState<T> {
  envelope: Envelope<T> | null;
  loading: boolean;
  error: StatsError | null;
  retry: () => void;
}

/**
 * Maps a request failure to a state the UI can show. A network, DNS or CORS
 * failure never reaches the AL, so it never gets a `response`: the shared
 * interceptor turns that into a bare TypeError, so a missing `response` is
 * treated as unreachable rather than read for a message. Exported for tests.
 */
export function toStatsError(error: unknown): StatsError {
  const response = (error as { response?: { status?: number; data?: { error?: string } } } | null)?.response;
  if (!response) return { kind: "failed", message: "Could not reach the Audience Layer." };
  const status = response.status;
  const message = response.data?.error ?? "Request failed";
  if (status === 401) return { kind: "forbidden", message: "Your sign-in is not accepted by the Audience Layer." };
  if (status === 403) return { kind: "forbidden", message: "You do not have access to Audience Layer stats." };
  if (status === 503 && message === "stats_query_timeout") {
    return { kind: "timeout", message: "Too slow for this range. Try a shorter one." };
  }
  if (status === 503 && message === "stats_busy") {
    return { kind: "timeout", message: "The stats service is busy. Try again in a moment." };
  }
  if (status === 400 && message === "tenant: no tenants are configured") {
    return { kind: "bad-request", message: "No tenants are set up in the Audience Layer yet." };
  }
  if (status === 400) return { kind: "bad-request", message };
  return { kind: "failed", message };
}

/**
 * One AL endpoint's answer for the current query. Refetches when the query or
 * reloadKey changes; with pollMs, refetches on that interval while mounted and
 * the browser tab is visible. The previous answer stays on screen while a
 * refetch is in flight.
 */
export function useStats<T>(path: string, query: string, reloadKey: number, pollMs?: number): StatsState<T> {
  const [state, setState] = useState<Omit<StatsState<T>, "retry">>({ envelope: null, loading: true, error: null });
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if (pollMs && !controller.signal.aborted) timer = setTimeout(load, pollMs);
    };
    const load = async () => {
      if (pollMs && document.hidden) return schedule();
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const envelope = await getStats<T>(path, query, controller.signal);
        if (!controller.signal.aborted) setState({ envelope, loading: false, error: null });
      } catch (error) {
        // The shared axios interceptor turns a cancellation into a TypeError,
        // so a cancelled request is recognised by its signal, not its error.
        if (!controller.signal.aborted) setState({ envelope: null, loading: false, error: toStatsError(error) });
      }
      schedule();
    };
    load();
    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [path, query, reloadKey, retryKey, pollMs]);

  return { ...state, retry: () => setRetryKey((k) => k + 1) };
}
